import { useState, useEffect, useRef } from "react";

/* ── GEMINI API KEY (from Vercel env variable) ─────────────── */
const GEMINI_KEY = process.env.REACT_APP_GEMINI_KEY;
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${GEMINI_KEY}`;

/* ── WEB3FORMS KEY — replace with yours from web3forms.com ─── */
const W3F_KEY = "YOUR_WEB3FORMS_KEY_HERE";

/* ── DESIGN TOKENS ─────────────────────────────────────────── */
const C = {
  bg:      "#060A0E",
  bg2:     "#0A1018",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.07)",
  borderA: "rgba(38,193,110,0.25)",
  grad:    "linear-gradient(135deg,#26C16E,#388BF8)",
  accent:  "#26C16E",
  accentD: "rgba(38,193,110,0.1)",
  accentB: "rgba(38,193,110,0.22)",
  blue:    "#388BF8",
  blueD:   "rgba(56,139,248,0.1)",
  orange:  "#FB923C",
  orangeD: "rgba(251,146,60,0.1)",
  green:   "#26C16E",
  greenD:  "rgba(38,193,110,0.1)",
  red:     "#F87171",
  redD:    "rgba(248,113,113,0.1)",
  purple:  "#A78BFA",
  text:    "#EEF4FF",
  sub:     "#8BA8C8",
  muted:   "#3D5470",
  syne:    "'Rajdhani',sans-serif",
  inst:    "'Exo 2',sans-serif",
};

const GS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Exo+2:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  ::-webkit-scrollbar{width:3px}
  ::-webkit-scrollbar-thumb{background:rgba(38,193,110,0.3);border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes blink{50%{opacity:0}}
  .fu{animation:fadeUp 0.4s ease forwards}
  .fi{animation:fadeIn 0.35s ease forwards}
  .pulse{animation:pulse 2s ease infinite}
  .spin{animation:spin 1s linear infinite; display:inline-block}
  .cursor{animation:blink 0.9s step-end infinite}
  @media print{
    .no-print{display:none!important}
    body{background:white!important}
    .print-area{background:white!important;color:#111!important}
  }
`;

/* ── MODULE 2 — INTAKE FORM DATA ───────────────────────────── */
const INDUSTRIES = [
  "Manufacturing & Textile","Agriculture & Food","Healthcare & Pharma",
  "Retail & E-commerce","Finance & Banking","Logistics & Supply Chain",
  "Education","Real Estate","Energy & Utilities","Construction",
  "Telecom","Government / Public Sector","Other",
];
const SIZES     = ["1–10","11–50","51–200","201–1000","1000+"];
const CITIES    = ["Karachi","Lahore","Islamabad / Rawalpindi","Faisalabad","Multan","Peshawar","Quetta","Sialkot","Gujranwala","Other"];
const REVENUES  = ["Under PKR 10M","PKR 10M–50M","PKR 50M–200M","PKR 200M–1B","PKR 1B+","Prefer not to say"];
const BUDGETS   = ["Under $2,000","$2,000–$10,000","$10,000–$50,000","$50,000–$200,000","$200,000+","Open — depends on ROI"];
const TOOLS     = ["None / Paper only","Excel & Email","WhatsApp Business","Basic ERP / POS","CRM System","Cloud Software","Custom Systems / APIs"];
const DEPTS     = ["Operations","Finance / Accounts","HR & Recruitment","Sales & Marketing","Customer Service","Supply Chain","Production / Manufacturing","IT / Technology"];
const URGENCY   = [{v:"explore",l:"Just exploring"},{v:"6mo",l:"Within 6 months"},{v:"3mo",l:"Within 3 months"},{v:"now",l:"Urgent — need now"}];

const CHALLENGES = [
  {id:"c1",l:"Manual repetitive tasks eating time",icon:"🔁"},
  {id:"c2",l:"High error / defect rates",icon:"⚠️"},
  {id:"c3",l:"Slow customer response times",icon:"💬"},
  {id:"c4",l:"Demand / inventory forecasting failures",icon:"📉"},
  {id:"c5",l:"Fraud or financial irregularities",icon:"🔐"},
  {id:"c6",l:"Supply chain inefficiencies",icon:"🚚"},
  {id:"c7",l:"No data-driven decision making",icon:"📊"},
  {id:"c8",l:"High HR / recruitment overhead",icon:"👥"},
  {id:"c9",l:"Quality control issues",icon:"🔬"},
  {id:"c10",l:"Language barriers (Urdu/English)",icon:"🗣️"},
  {id:"c11",l:"Inventory mismanagement",icon:"📦"},
  {id:"c12",l:"Slow financial / management reporting",icon:"📑"},
  {id:"c13",l:"Difficulty scheduling & coordination",icon:"📅"},
  {id:"c14",l:"Customer complaints & retention",icon:"😤"},
];

const GOALS = [
  {id:"g1",l:"Cut operational costs"},
  {id:"g2",l:"Increase revenue"},
  {id:"g3",l:"Automate repetitive work"},
  {id:"g4",l:"Better customer experience"},
  {id:"g5",l:"Scale without hiring more staff"},
  {id:"g6",l:"Data-driven decisions"},
  {id:"g7",l:"Compete with larger firms"},
  {id:"g8",l:"Regulatory compliance"},
  {id:"g9",l:"Improve product / service quality"},
];

/* ── MODULE 3 — SENIOR PARTNER BRIEFING PROMPT ─────────────── */
function buildSeniorPartnerPrompt(form) {
  return `You are a Senior AI Consulting Partner. You are preparing for an AI audit with the following organisation.

INTAKE FORM RESPONSES:
- Company: ${form.companyName}
- Industry: ${form.industry}
- Department being audited: ${form.department}
- Team size: ${form.companySize} employees
- City: ${form.city}, Pakistan
- Annual Revenue: ${form.revenue}
- Current tools in use: ${form.currentTools.join(", ")||"None specified"}
- Current tech level: ${form.techLevel}
- Data availability: ${form.dataLevel}
- Annual AI/tech budget: ${form.budget}
- Implementation urgency: ${form.urgency}
- Key challenges: ${form.challenges.join(", ")}
- Business goals: ${form.goals.join(", ")}
- Additional context: ${form.additionalInfo||"None"}

You must respond with ONLY a valid JSON object. No markdown, no explanation, no preamble. Just the JSON.

{
  "businessSnapshot": "<2-3 sentences: how this specific company/department generates output, what their core processes are, where operational cost is concentrated — specific to their industry and Pakistan context>",
  "investigationZones": [
    {
      "zone": "<name of operational area>",
      "likelyWaste": "<specific description of how time/money is being lost here>",
      "roiPotential": "High|Medium",
      "questions": [
        "<specific question 1 to quantify the problem>",
        "<specific question 2 to understand the process>",
        "<specific question 3 to assess cost/time impact>"
      ]
    }
  ],
  "topZoneRanking": "<name of zone with highest ROI potential and brief reason why>",
  "pakistanContext": "<1-2 sentences on specific Pakistan factors — Urdu language, SMEDA, SBP regulations, local infrastructure, or sector-specific context that affects AI adoption>"
}

Rules:
- investigationZones: exactly 5 zones
- All output must be specific to THIS company — not generic
- Questions must be designed to extract hrs/week, frequency, role, and cost data per Module 4 quantification`
  ;
}

/* ── MODULE 4 — QUANTIFICATION + MODULE 5 LEAD INVESTIGATOR ── */
function buildLeadInvestigatorPrompt(form, zones) {
  const challengeText = form.challenges.join(", ");
  const goalText = form.goals.join(", ");

  return `You are the Lead AI Audit Investigator conducting a full AI Opportunities audit for a Pakistani business. Apply the complete 7-module audit framework.

COMPANY PROFILE:
- Name: ${form.companyName}
- Industry: ${form.industry}  
- Department: ${form.department}
- Size: ${form.companySize} employees, ${form.city}
- Revenue: ${form.revenue}
- Current tools: ${form.currentTools.join(", ")||"None"}
- Tech level: ${form.techLevel}
- Data level: ${form.dataLevel}
- Budget: ${form.budget}
- Urgency: ${form.urgency}
- Challenges: ${challengeText}
- Goals: ${goalText}
- Context: ${form.additionalInfo||"None"}

INVESTIGATION ZONES IDENTIFIED:
${zones.map((z,i)=>`${i+1}. ${z.zone} — ${z.likelyWaste} (ROI Potential: ${z.roiPotential})`).join("\n")}

MODULE 4 — QUANTIFICATION FORMULA:
Annual Cost of Inefficiency = Hours/week × Hourly Rate (PKR) × 52
Conservative AI Saving: 40-60% for partial automation, 70-90% for full automation of discrete tasks
Net Annual Benefit = Annual Recoverable Value - Tool Licensing Cost

MODULE 5 — PRODUCE THE 4-SECTION AI OPPORTUNITIES REPORT:

You must respond with ONLY a valid JSON object. No markdown, no preamble, no explanation.

{
  "overallScore": <integer 0-100>,
  "readinessLabel": "<Beginner|Developing|Ready|Advanced>",

  "section1_diagnosis": {
    "auditScope": "<1 paragraph: what was audited, key operational areas examined, Pakistan-specific context>",
    "findings": [
      {
        "title": "<name of inefficiency>",
        "currentProcess": "<how it works today — specific and detailed>",
        "hoursPerWeek": <number>,
        "rolePerforming": "<job title>",
        "hourlyRatePKR": <number>,
        "annualCostPKR": <number — must equal hoursPerWeek × hourlyRatePKR × 52>,
        "stakeholderQuote": "<realistic quote a Pakistani business owner might say about this pain>",
        "investigationZone": "<which zone this came from>"
      }
    ],
    "totalAnnualCostPKR": <sum of all finding annualCostPKR>,
    "competitiveAlert": "<1-2 sentences: what competitors in this Pakistan industry are doing with AI — create urgency>"
  },

  "section2_opportunities": [
    {
      "findingTitle": "<matches a finding title from section 1>",
      "recommendedTool": "<specific named off-the-shelf tool — not a category>",
      "toolDescription": "<what it does in 1 sentence>",
      "howItFits": "<exactly how this tool solves THIS company's specific problem>",
      "beforeWorkflow": "<current manual process — specific>",
      "afterWorkflow": "<AI-assisted process — specific>",
      "hoursRecoveredPerWeek": <conservative number>,
      "annualSavingPKR": <conservative calculation>,
      "toolLicenseCostUSD": <annual cost in USD>,
      "effort": "Low|Medium|High",
      "timeline": "<e.g. 3-4 weeks>",
      "pakistanNote": "<why this tool works specifically in Pakistan — Urdu support, local compliance, WhatsApp integration, etc.>"
    }
  ],

  "section3_businessCase": {
    "combinedAnnualSavingPKR": <sum of all opportunity annualSavingPKR>,
    "totalToolCostUSD": <sum of all tool costs>,
    "netAnnualBenefitPKR": <combinedAnnualSavingPKR minus tool costs converted to PKR at 280>,
    "paybackPeriodMonths": <calculated: totalToolCostUSD × 280 / (combinedAnnualSavingPKR/12)>,
    "operationalForecast90Days": "<2-3 sentences: what the business will look like in 90 days if they implement — specific and optimistic but realistic>",
    "roiMultiple": "<e.g. 8x — every PKR 1 invested returns PKR 8>",
    "dimensions": [
      {"label":"Data Readiness","score":<0-100>,"insight":"<1 sentence>"},
      {"label":"Tech Infrastructure","score":<0-100>,"insight":"<1 sentence>"},
      {"label":"Process Maturity","score":<0-100>,"insight":"<1 sentence>"},
      {"label":"Leadership Buy-in","score":<0-100>,"insight":"<1 sentence>"},
      {"label":"Talent & Skills","score":<0-100>,"insight":"<1 sentence>"},
      {"label":"Financial Readiness","score":<0-100>,"insight":"<1 sentence>"}
    ]
  },

  "section4_nextSteps": [
    {
      "action": "<specific action>",
      "owner": "<who does it — role title>",
      "timeline": "<e.g. Week 1>",
      "detail": "<exactly what needs to happen>"
    }
  ],

  "topRisks": [
    {"risk":"<title>","description":"<Pakistan-specific risk>","mitigation":"<concrete action>"},
    {"risk":"<title>","description":"<risk>","mitigation":"<mitigation>"},
    {"risk":"<title>","description":"<risk>","mitigation":"<mitigation>"}
  ]
}

Rules:
- section2_opportunities: exactly 3 items matching the top 3 findings
- section4_nextSteps: exactly 4 steps
- ALL financial figures in PKR (use PKR 280 = $1 for conversions)
- annualCostPKR MUST equal hoursPerWeek × hourlyRatePKR × 52 — no exceptions
- Recommend only specific named off-the-shelf tools — never "an AI tool" or generic categories
- All tools must be currently available and appropriate for Pakistan market
- Conservative estimates always — use lower bound of any range`;
}

/* ── GEMINI API CALL ────────────────────────────────────────── */
async function callGemini(prompt) {
  const res = await fetch(GEMINI_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 4096,
        responseMimeType: "application/json",
      },
    }),
  });
  if (!res.ok) throw new Error(`Gemini API error: ${res.status}`);
  const data = await res.json();
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || "{}";
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

/* ── SMALL UI COMPONENTS ────────────────────────────────────── */
const Btn = ({ children, onClick, variant = "primary", disabled, small, style = {} }) => {
  const base = {
    padding: small ? "7px 14px" : "11px 22px",
    borderRadius: 8, border: "none", cursor: disabled ? "not-allowed" : "pointer",
    fontSize: small ? 12 : 13, fontWeight: 600, fontFamily: C.inst,
    transition: "all 0.15s", letterSpacing: "0.01em", ...style,
  };
  const v = {
    primary: { background: disabled ? "rgba(255,255,255,0.06)" : C.grad, color: disabled ? C.muted : "#fff" },
    ghost:   { background: "transparent", border: `1px solid ${C.border}`, color: C.sub },
    green:   { background: C.greenD, color: C.green, border: `1px solid rgba(38,193,110,0.25)` },
    danger:  { background: C.redD, color: C.red, border: `1px solid rgba(248,113,113,0.2)` },
  };
  return <button onClick={!disabled ? onClick : undefined} style={{ ...base, ...(v[variant] || v.primary) }}>{children}</button>;
};

const ASelect = ({ options, value, onChange, placeholder }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={{
    width: "100%", padding: "10px 14px", borderRadius: 8,
    border: `1px solid ${value ? C.accentB : C.border}`,
    background: "rgba(255,255,255,0.04)", color: value ? C.text : C.muted,
    fontSize: 13, fontFamily: C.inst, outline: "none", appearance: "none", cursor: "pointer",
  }}>
    <option value="" disabled>{placeholder}</option>
    {options.map(o => <option key={o} value={o} style={{ background: "#0D1117" }}>{o}</option>)}
  </select>
);

const AInput = ({ value, onChange, placeholder, type = "text" }) => (
  <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type}
    style={{
      width: "100%", padding: "10px 14px", borderRadius: 8,
      border: `1px solid ${value ? C.accentB : C.border}`,
      background: "rgba(255,255,255,0.04)", color: C.text,
      fontSize: 13, fontFamily: C.inst, outline: "none",
    }} />
);

const Chip = ({ label, icon, selected, onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8,
    border: `1px solid ${selected ? C.accentB : C.border}`,
    background: selected ? C.accentD : "rgba(255,255,255,0.02)",
    color: selected ? C.accent : C.sub, cursor: "pointer",
    fontSize: 12, fontFamily: C.inst, transition: "all 0.15s",
  }}>
    {icon && <span>{icon}</span>}{label}
  </button>
);

const Radio = ({ label, selected, onClick }) => (
  <button onClick={onClick} style={{
    width: "100%", padding: "9px 14px", borderRadius: 8, textAlign: "left",
    border: `1px solid ${selected ? C.accentB : C.border}`,
    background: selected ? C.accentD : "rgba(255,255,255,0.02)",
    cursor: "pointer", transition: "all 0.15s",
    display: "flex", alignItems: "center", gap: 10,
  }}>
    <div style={{
      width: 14, height: 14, borderRadius: "50%",
      border: `2px solid ${selected ? C.accent : C.muted}`,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      {selected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />}
    </div>
    <span style={{ fontSize: 13, color: selected ? C.accent : C.text, fontFamily: C.inst }}>{label}</span>
  </button>
);

const MultiSelect = ({ options, selected, onToggle, label }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: C.syne, marginBottom: 8 }}>{label}</div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
      {options.map(o => (
        <button key={o} onClick={() => onToggle(o)} style={{
          padding: "6px 12px", borderRadius: 6, fontSize: 12, fontFamily: C.inst, cursor: "pointer",
          border: `1px solid ${selected.includes(o) ? C.accentB : C.border}`,
          background: selected.includes(o) ? C.accentD : "rgba(255,255,255,0.02)",
          color: selected.includes(o) ? C.accent : C.sub, transition: "all 0.15s",
        }}>{o}</button>
      ))}
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: C.syne, marginBottom: 8 }}>{children}</div>
);

const ProgressBar = ({ pct, color = C.accent }) => (
  <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 2, background: color, transition: "width 1s ease" }} />
  </div>
);

const PKR = (n) => `PKR ${Number(n).toLocaleString("en-PK")}`;

/* ── SCAN STEPS (Module 3 → 5) ──────────────────────────────── */
const SCAN_STEPS = [
  { icon: "🔍", label: "Module 2 — Processing intake form responses" },
  { icon: "🧠", label: "Module 3 — Running Senior Partner Briefing Protocol" },
  { icon: "🏭", label: "Module 3 — Identifying 5 investigation zones" },
  { icon: "📐", label: "Module 4 — Applying quantification formula (hrs × rate × 52)" },
  { icon: "🔎", label: "Module 5 — Lead Investigator Stage 1: establishing findings" },
  { icon: "💡", label: "Module 5 — Lead Investigator Stage 2: ranking by ROI impact" },
  { icon: "📋", label: "Module 5 — Compiling 4-section AI Opportunities Report" },
];

/* ── REPORT SECTION COMPONENTS ──────────────────────────────── */
const SectionHeader = ({ num, title, color = C.accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
    <div style={{ width: 28, height: 28, borderRadius: 7, background: `${color}20`, border: `1px solid ${color}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color, fontFamily: C.syne, flexShrink: 0 }}>{num}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: C.syne, letterSpacing: "0.05em", textTransform: "uppercase" }}>{title}</div>
  </div>
);

/* ── MAIN AUDIT TOOL COMPONENT ──────────────────────────────── */
export default function AuditTool({ onBack }) {
  const [step, setStep] = useState(0); // 0-4 = form steps, 5 = scanning, 6 = report
  const [form, setForm] = useState({
    companyName: "", industry: "", department: "", companySize: "",
    city: "", revenue: "", currentTools: [], techLevel: "", dataLevel: "",
    budget: "", urgency: "", challenges: [], goals: [], additionalInfo: "",
    contactName: "", contactEmail: "", contactPhone: "",
  });
  const [scanIdx, setScanIdx] = useState(0);
  const [zones, setZones] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [leadState, setLeadState] = useState("idle"); // idle | sending | done | fail
  const reportRef = useRef(null);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const tog = (k, v) => setForm(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));

  const canNext = () => {
    if (step === 0) return form.companyName && form.industry && form.department && form.companySize && form.city && form.revenue;
    if (step === 1) return form.currentTools.length > 0 && form.techLevel && form.dataLevel && form.budget;
    if (step === 2) return form.challenges.length >= 2;
    if (step === 3) return form.goals.length >= 1 && form.urgency;
    return true;
  };

  /* ── RUN THE FULL AUDIT ─────────────────────────────────── */
  const runAudit = async () => {
    setStep(5);
    setScanIdx(0);
    setError("");

    const challengeLabels = form.challenges.map(id => CHALLENGES.find(c => c.id === id)?.l || id);
    const goalLabels = form.goals.map(id => GOALS.find(g => g.id === id)?.l || id);
    const enrichedForm = { ...form, challenges: challengeLabels, goals: goalLabels };

    try {
      // Module 3 — Senior Partner Protocol
      setScanIdx(1);
      await new Promise(r => setTimeout(r, 600));
      setScanIdx(2);
      const zoneData = await callGemini(buildSeniorPartnerPrompt(enrichedForm));
      const zoneList = zoneData.investigationZones || [];
      setZones(zoneList);

      // Module 4 + 5 — Quantification + Lead Investigator
      setScanIdx(3);
      await new Promise(r => setTimeout(r, 600));
      setScanIdx(4);
      await new Promise(r => setTimeout(r, 600));
      setScanIdx(5);
      await new Promise(r => setTimeout(r, 600));
      setScanIdx(6);

      const reportData = await callGemini(buildLeadInvestigatorPrompt(enrichedForm, zoneList));
      setReport(reportData);
      setStep(6);
    } catch (e) {
      setError(e.message || "Audit failed. Please try again.");
      setStep(4);
    }
  };

  /* ── SEND LEAD VIA WEB3FORMS ────────────────────────────── */
  const sendLead = async () => {
    if (!form.contactName || !form.contactEmail) return;
    setLeadState("sending");
    const summary = `
AIPARTNERS.PK — NEW AUDIT LEAD
================================
Company:    ${form.companyName}
Industry:   ${form.industry}
Department: ${form.department}
City:       ${form.city}, Pakistan
Size:       ${form.companySize} employees
Revenue:    ${form.revenue}
Budget:     ${form.budget}
Urgency:    ${form.urgency}

AI SCORE: ${report?.overallScore}/100 (${report?.readinessLabel})
Total Annual Cost Identified: ${PKR(report?.section1_diagnosis?.totalAnnualCostPKR || 0)}
Net Annual Benefit: ${PKR(report?.section3_businessCase?.netAnnualBenefitPKR || 0)}
Payback: ${report?.section3_businessCase?.paybackPeriodMonths} months

CONTACT:
Name:  ${form.contactName}
Email: ${form.contactEmail}
Phone: ${form.contactPhone || "Not provided"}

Top Challenge: ${form.challenges[0] || ""}
Top Goal: ${form.goals[0] || ""}
    `.trim();

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: W3F_KEY,
          subject: `🚀 New Audit Lead — ${form.companyName} (${report?.overallScore}/100)`,
          from_name: "AIpartners.pk Audit System",
          name: form.contactName,
          email: form.contactEmail,
          phone: form.contactPhone || "N/A",
          company: form.companyName,
          score: `${report?.overallScore}/100`,
          message: summary,
          botcheck: "",
        }),
      });
      const data = await res.json();
      setLeadState(data.success ? "done" : "fail");
    } catch {
      setLeadState("fail");
    }
  };

  /* ── PRINT REPORT ────────────────────────────────────────── */
  const printReport = () => window.print();

  /* ── STEP PROGRESS BAR ──────────────────────────────────── */
  const StepBar = () => (
    <div style={{ display: "flex", gap: 5, marginBottom: 24 }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{
          flex: 1, height: 2, borderRadius: 2,
          background: i < step ? C.accent : i === step ? "rgba(38,193,110,0.4)" : "rgba(255,255,255,0.07)",
          transition: "background 0.4s",
        }} />
      ))}
    </div>
  );

  const cardStyle = { background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "28px" };

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "28px 20px", fontFamily: C.inst }}>
      <style>{GS}</style>

      <div style={{ maxWidth: 680, margin: "0 auto" }}>

        {/* ── TOP BAR ─────────────────────────────────────── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }} className="no-print">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accentD, border: `1px solid ${C.accentB}`, display: "flex", alignItems: "center", justifyContent: "center" }}>🇵🇰</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: C.syne }}>
                <span style={{ background: C.grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI</span>
                <span style={{ color: C.text }}>PARTNERS.PK</span>
              </div>
              <div style={{ fontSize: 9, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>7-Module AI Audit Framework</div>
            </div>
          </div>
          {onBack && <Btn variant="ghost" small onClick={onBack}>← Back</Btn>}
        </div>

        {/* ══ STEP 0 — COMPANY INFO (Module 2) ═══════════════ */}
        {step === 0 && (
          <div style={cardStyle} className="fi">
            <StepBar />
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: C.syne, marginBottom: 4 }}>Module 2 — Company Intake</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 22 }}>The more detail you provide, the more accurate the audit.</div>

            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Company Name</FieldLabel>
              <AInput value={form.companyName} onChange={v => upd("companyName", v)} placeholder="e.g. Crescent Textile Mills Ltd." />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div>
                <FieldLabel>Industry Sector</FieldLabel>
                <ASelect options={INDUSTRIES} value={form.industry} onChange={v => upd("industry", v)} placeholder="Select industry" />
              </div>
              <div>
                <FieldLabel>Department Being Audited</FieldLabel>
                <ASelect options={DEPTS} value={form.department} onChange={v => upd("department", v)} placeholder="Select department" />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div>
                <FieldLabel>Company Size</FieldLabel>
                <ASelect options={SIZES.map(s => s + " employees")} value={form.companySize} onChange={v => upd("companySize", v)} placeholder="Size" />
              </div>
              <div>
                <FieldLabel>City</FieldLabel>
                <ASelect options={CITIES} value={form.city} onChange={v => upd("city", v)} placeholder="City" />
              </div>
              <div>
                <FieldLabel>Annual Revenue</FieldLabel>
                <ASelect options={REVENUES} value={form.revenue} onChange={v => upd("revenue", v)} placeholder="Revenue" />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
              <Btn onClick={() => canNext() && setStep(1)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* ══ STEP 1 — TECH & DATA (Module 2 continued) ══════ */}
        {step === 1 && (
          <div style={cardStyle} className="fi">
            <StepBar />
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: C.syne, marginBottom: 4 }}>Module 2 — Technology Landscape</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 22 }}>Honest answers help us recommend the right level of AI for where you are now.</div>

            <MultiSelect options={TOOLS} selected={form.currentTools} onToggle={v => tog("currentTools", v)} label="Tools currently in use (select all that apply)" />

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Current Technology Level</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {[
                  { v: "0", l: "Paper-based / no digital systems" },
                  { v: "2", l: "Basic — Excel, WhatsApp, email only" },
                  { v: "4", l: "Moderate — some software or ERP" },
                  { v: "7", l: "Advanced — cloud systems and APIs" },
                  { v: "9", l: "Cutting-edge — data pipelines and analytics" },
                ].map(o => <Radio key={o.v} label={o.l} selected={form.techLevel === o.v} onClick={() => upd("techLevel", o.v)} />)}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Data Availability & Quality</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {[
                  { v: "0", l: "No data collected or stored" },
                  { v: "2", l: "Some records in Excel or paper files" },
                  { v: "5", l: "Structured data in a business system" },
                  { v: "7", l: "Multi-source data, partially integrated" },
                  { v: "9", l: "Clean, rich, integrated data warehouse" },
                ].map(o => <Radio key={o.v} label={o.l} selected={form.dataLevel === o.v} onClick={() => upd("dataLevel", o.v)} />)}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Annual AI / Tech Budget</FieldLabel>
              <ASelect options={BUDGETS} value={form.budget} onChange={v => upd("budget", v)} placeholder="Select budget range" />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
              <Btn variant="ghost" onClick={() => setStep(0)}>← Back</Btn>
              <Btn onClick={() => canNext() && setStep(2)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* ══ STEP 2 — CHALLENGES ═════════════════════════════ */}
        {step === 2 && (
          <div style={cardStyle} className="fi">
            <StepBar />
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: C.syne, marginBottom: 4 }}>Module 2 — Operational Challenges</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 22 }}>Select every challenge you face — this maps directly to your investigation zones. Pick at least 2.</div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {CHALLENGES.map(c => (
                <Chip key={c.id} label={c.l} icon={c.icon} selected={form.challenges.includes(c.id)} onClick={() => tog("challenges", c.id)} />
              ))}
            </div>
            {form.challenges.length > 0 && (
              <div style={{ fontSize: 12, color: C.accent, marginBottom: 16, fontFamily: C.inst }}>
                {form.challenges.length} challenge{form.challenges.length > 1 ? "s" : ""} selected
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Any additional context? (Optional)</FieldLabel>
              <textarea value={form.additionalInfo} onChange={e => upd("additionalInfo", e.target.value)}
                placeholder="Describe any specific constraints, recent problems, or context you want the AI auditor to consider..."
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 8,
                  border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.04)",
                  color: C.text, fontSize: 13, fontFamily: C.inst, resize: "vertical",
                  minHeight: 80, outline: "none",
                }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Btn variant="ghost" onClick={() => setStep(1)}>← Back</Btn>
              <Btn onClick={() => canNext() && setStep(3)} disabled={!canNext()}>Continue →</Btn>
            </div>
          </div>
        )}

        {/* ══ STEP 3 — GOALS & URGENCY ════════════════════════ */}
        {step === 3 && (
          <div style={cardStyle} className="fi">
            <StepBar />
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: C.syne, marginBottom: 4 }}>Module 2 — Goals & Timeline</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 22 }}>Your goals determine which AI initiatives we prioritise in the roadmap.</div>

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Business Goals (select all that apply)</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {GOALS.map(g => (
                  <Chip key={g.id} label={g.l} selected={form.goals.includes(g.id)} onClick={() => tog("goals", g.id)} />
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <FieldLabel>Implementation Urgency</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {URGENCY.map(o => <Radio key={o.v} label={o.l} selected={form.urgency === o.v} onClick={() => upd("urgency", o.v)} />)}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Btn variant="ghost" onClick={() => setStep(2)}>← Back</Btn>
              <Btn onClick={() => { if (canNext()) runAudit(); }} disabled={!canNext()}>
                Run AI Audit — Module 3 to 5 →
              </Btn>
            </div>
          </div>
        )}

        {/* ══ STEP 5 — SCANNING ═══════════════════════════════ */}
        {step === 5 && (
          <div style={cardStyle} className="fi">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 12px", borderRadius: 20, background: C.accentD, border: `1px solid ${C.accentB}`, marginBottom: 16 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, boxShadow: `0 0 6px ${C.accent}` }} className="pulse" />
              <span style={{ fontSize: 11, color: C.accent, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: C.syne }}>Audit in progress</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: C.syne, marginBottom: 20 }}>
              Analysing <span style={{ background: C.grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{form.companyName}</span>
            </div>
            {SCAN_STEPS.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", opacity: i <= scanIdx ? 1 : 0.15, transition: "opacity 0.5s" }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
                  background: i < scanIdx ? C.greenD : i === scanIdx ? C.accentD : "rgba(255,255,255,0.04)",
                  border: `1px solid ${i < scanIdx ? C.accentB : i === scanIdx ? C.accentB : C.border}`,
                }}>
                  {i < scanIdx ? "✓" : i === scanIdx ? <span className="spin" style={{ fontSize: 10 }}>↻</span> : "·"}
                </div>
                <span style={{ fontSize: 12, color: i < scanIdx ? C.accent : i === scanIdx ? C.text : C.muted, fontFamily: C.inst }}>{s.label}</span>
              </div>
            ))}
            {error && (
              <div style={{ marginTop: 20, padding: 14, borderRadius: 8, background: C.redD, border: `1px solid rgba(248,113,113,0.2)`, fontSize: 13, color: C.red, fontFamily: C.inst }}>
                {error}
                <button onClick={() => { setStep(3); setError(""); }} style={{ marginLeft: 10, color: C.accent, background: "none", border: "none", cursor: "pointer", fontFamily: C.inst, fontSize: 13 }}>Try again</button>
              </div>
            )}
          </div>
        )}

        {/* ══ STEP 6 — FULL REPORT ════════════════════════════ */}
        {step === 6 && report && (
          <div ref={reportRef}>

            {/* Report header */}
            <div style={{ padding: "20px 24px", borderRadius: 14, background: C.accentD, border: `1px solid ${C.accentB}`, marginBottom: 20 }} className="print-area">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: C.syne, marginBottom: 4 }}>AI Opportunities Report</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.text, fontFamily: C.syne }}>{form.companyName}</div>
                  <div style={{ fontSize: 12, color: C.sub, fontFamily: C.inst }}>{form.industry} · {form.department} · {form.city}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 44, fontWeight: 800, color: report.overallScore >= 75 ? C.green : report.overallScore >= 50 ? C.accent : C.orange, fontFamily: C.syne, lineHeight: 1 }}>{report.overallScore}</div>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 2 }}>AI Score</div>
                  <div style={{ fontSize: 12, color: C.accent, fontWeight: 700, fontFamily: C.syne }}>{report.readinessLabel}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }} className="no-print">
                <Btn small onClick={printReport}>⬇ Print / Save PDF</Btn>
                <Btn small variant="ghost" onClick={() => { setStep(0); setReport(null); setError(""); }}>New Audit</Btn>
              </div>
            </div>

            {/* ── SECTION 1 — DIAGNOSIS ──────────────────────── */}
            <div style={{ padding: "22px 24px", borderRadius: 14, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 16 }} className="print-area fu">
              <SectionHeader num="1" title="The Diagnosis" color={C.accent} />
              <div style={{ fontSize: 13, color: C.sub, fontFamily: C.inst, lineHeight: 1.7, marginBottom: 16 }}>
                {report.section1_diagnosis?.auditScope}
              </div>

              {/* Competitive Alert */}
              <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 8, background: C.orangeD, border: "1px solid rgba(251,146,60,0.2)", marginBottom: 18 }}>
                <span style={{ flexShrink: 0 }}>⚡</span>
                <div style={{ fontSize: 12, color: "#FED7AA", fontFamily: C.inst, lineHeight: 1.6 }}>
                  <strong style={{ color: C.orange }}>Market Alert: </strong>{report.section1_diagnosis?.competitiveAlert}
                </div>
              </div>

              {/* Findings */}
              {report.section1_diagnosis?.findings?.map((f, i) => (
                <div key={i} style={{ marginBottom: 12, padding: "14px 16px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: C.syne }}>{f.title}</div>
                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.red, fontFamily: C.syne }}>{PKR(f.annualCostPKR)}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>/year cost</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: C.sub, fontFamily: C.inst, marginBottom: 10, lineHeight: 1.5 }}>{f.currentProcess}</div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: C.inst }}>⏱ {f.hoursPerWeek} hrs/week</span>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: C.inst }}>👤 {f.rolePerforming}</span>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: C.inst }}>💰 PKR {f.hourlyRatePKR}/hr</span>
                  </div>
                  {f.stakeholderQuote && (
                    <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(255,255,255,0.02)", borderLeft: `3px solid ${C.accent}`, fontSize: 12, color: C.sub, fontStyle: "italic", fontFamily: C.inst }}>
                      "{f.stakeholderQuote}"
                    </div>
                  )}
                </div>
              ))}

              {/* Total */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 8, background: C.redD, border: "1px solid rgba(248,113,113,0.2)", marginTop: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: C.syne }}>Total Identified Annual Cost</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: C.syne }}>{PKR(report.section1_diagnosis?.totalAnnualCostPKR)}</span>
              </div>
            </div>

            {/* ── SECTION 2 — OPPORTUNITIES ──────────────────── */}
            <div style={{ padding: "22px 24px", borderRadius: 14, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 16 }} className="print-area fu">
              <SectionHeader num="2" title="The Opportunities" color={C.blue} />
              {report.section2_opportunities?.map((op, i) => (
                <div key={i} style={{ marginBottom: 14, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
                  <div style={{ padding: "12px 16px", background: "rgba(56,139,248,0.06)", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: C.syne }}>{op.recommendedTool}</div>
                      <div style={{ fontSize: 11, color: C.sub, fontFamily: C.inst }}>For: {op.findingTitle}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: op.effort === "Low" ? C.greenD : C.accentD, color: op.effort === "Low" ? C.green : C.accent, fontWeight: 600 }}>{op.effort} effort</span>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: C.blueD, color: C.blue, fontWeight: 600 }}>{op.timeline}</span>
                    </div>
                  </div>
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ fontSize: 12, color: C.sub, fontFamily: C.inst, marginBottom: 12, lineHeight: 1.5 }}>{op.howItFits}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                      <div style={{ padding: "10px 12px", borderRadius: 8, background: C.redD, border: "1px solid rgba(248,113,113,0.15)" }}>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Before</div>
                        <div style={{ fontSize: 12, color: "#FCA5A5", fontFamily: C.inst, lineHeight: 1.5 }}>{op.beforeWorkflow}</div>
                      </div>
                      <div style={{ padding: "10px 12px", borderRadius: 8, background: C.greenD, border: `1px solid rgba(38,193,110,0.15)` }}>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>After</div>
                        <div style={{ fontSize: 12, color: "#86EFAC", fontFamily: C.inst, lineHeight: 1.5 }}>{op.afterWorkflow}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 12, color: C.green }}>📈 Save {PKR(op.annualSavingPKR)}/year</span>
                      <span style={{ fontSize: 12, color: C.sub }}>⏱ {op.hoursRecoveredPerWeek} hrs/week recovered</span>
                      <span style={{ fontSize: 12, color: C.sub }}>💰 Tool cost ~${op.toolLicenseCostUSD}/year</span>
                    </div>
                    {op.pakistanNote && (
                      <div style={{ marginTop: 8, padding: "7px 12px", borderRadius: 6, background: C.blueD, border: "1px solid rgba(56,139,248,0.2)", fontSize: 11, color: C.blue, fontFamily: C.inst }}>
                        🇵🇰 {op.pakistanNote}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── SECTION 3 — BUSINESS CASE ──────────────────── */}
            <div style={{ padding: "22px 24px", borderRadius: 14, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 16 }} className="print-area fu">
              <SectionHeader num="3" title="The Business Case" color={C.green} />

              {/* ROI Numbers */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Annual Saving", value: PKR(report.section3_businessCase?.combinedAnnualSavingPKR), color: C.green },
                  { label: "Tool Costs", value: `$${report.section3_businessCase?.totalToolCostUSD}/yr`, color: C.orange },
                  { label: "Net Benefit", value: PKR(report.section3_businessCase?.netAnnualBenefitPKR), color: C.accent },
                  { label: "Payback", value: `${report.section3_businessCase?.paybackPeriodMonths} months`, color: C.blue },
                ].map((r, i) => (
                  <div key={i} style={{ padding: "12px 14px", borderRadius: 10, background: `${r.color}08`, border: `1px solid ${r.color}20`, textAlign: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: r.color, fontFamily: C.syne, marginBottom: 3 }}>{r.value}</div>
                    <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{r.label}</div>
                  </div>
                ))}
              </div>

              {/* ROI Multiple */}
              <div style={{ padding: "12px 16px", borderRadius: 8, background: C.accentD, border: `1px solid ${C.accentB}`, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: C.sub, fontFamily: C.inst }}>Return on Investment</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: C.accent, fontFamily: C.syne }}>{report.section3_businessCase?.roiMultiple}</span>
              </div>

              {/* 90-day forecast */}
              <div style={{ padding: "14px 16px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6, fontFamily: C.syne }}>90-Day Operational Forecast</div>
                <div style={{ fontSize: 13, color: C.sub, fontFamily: C.inst, lineHeight: 1.7 }}>{report.section3_businessCase?.operationalForecast90Days}</div>
              </div>

              {/* 6-Dimension Scores */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {report.section3_businessCase?.dimensions?.map((d, i) => {
                  const dc = d.score >= 60 ? C.green : d.score >= 40 ? C.accent : C.red;
                  return (
                    <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: C.inst }}>{d.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: dc, fontFamily: C.syne }}>{d.score}</span>
                      </div>
                      <ProgressBar pct={d.score} color={dc} />
                      <div style={{ fontSize: 11, color: C.muted, fontFamily: C.inst, marginTop: 5 }}>{d.insight}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── SECTION 4 — NEXT STEPS ─────────────────────── */}
            <div style={{ padding: "22px 24px", borderRadius: 14, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 16 }} className="print-area fu">
              <SectionHeader num="4" title="Next Steps" color={C.purple} />
              {report.section4_nextSteps?.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 14, marginBottom: 14, padding: "12px 16px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}` }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.purpleD, border: "1px solid rgba(167,139,250,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: C.purple, fontFamily: C.syne, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: C.syne }}>{s.action}</div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 10 }}>
                        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: C.blueD, color: C.blue }}>{s.timeline}</span>
                        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "rgba(255,255,255,0.04)", color: C.muted }}>{s.owner}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: C.sub, fontFamily: C.inst, lineHeight: 1.5 }}>{s.detail}</div>
                  </div>
                </div>
              ))}

              {/* Risks */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, color: C.orange, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: C.syne, marginBottom: 12 }}>⚠️ Top Risks & Mitigations</div>
                {report.topRisks?.map((r, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, marginBottom: 8 }}>
                    <div style={{ padding: "10px 14px", background: C.orangeD, borderRight: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.orange, marginBottom: 3 }}>{r.risk}</div>
                      <div style={{ fontSize: 11, color: "#FED7AA", fontFamily: C.inst }}>{r.description}</div>
                    </div>
                    <div style={{ padding: "10px 14px", background: C.greenD }}>
                      <div style={{ fontSize: 10, color: C.green, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Mitigation</div>
                      <div style={{ fontSize: 11, color: "#86EFAC", fontFamily: C.inst }}>{r.mitigation}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── LEAD CAPTURE / CTA ─────────────────────────── */}
            <div style={{ padding: "24px 28px", borderRadius: 14, border: `1px solid ${C.accentB}`, background: "linear-gradient(135deg,rgba(38,193,110,0.07),rgba(56,139,248,0.07))", marginBottom: 20 }} className="no-print fu">

              {leadState === "done" ? (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontFamily: C.syne, marginBottom: 8 }}>Report sent to your inbox!</div>
                  <div style={{ fontSize: 13, color: C.sub, fontFamily: C.inst, marginBottom: 20, lineHeight: 1.6 }}>
                    Our team will review your audit and contact you within 24 hours with a personalised proposal.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 400, margin: "0 auto 20px" }}>
                    {[
                      { n: "1", t: "Check email for your PDF report", time: "In minutes" },
                      { n: "2", t: "Team reviews your findings", time: "Within 2 hours" },
                      { n: "3", t: "You receive a proposal + consultation invite", time: "Within 24 hours" },
                    ].map((s, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 14px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.accentD, border: `1px solid ${C.accentB}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: C.accent, flexShrink: 0 }}>{s.n}</div>
                        <span style={{ flex: 1, fontSize: 12, color: C.text, fontFamily: C.inst }}>{s.t}</span>
                        <span style={{ fontSize: 10, color: C.muted, fontFamily: C.inst }}>{s.time}</span>
                      </div>
                    ))}
                  </div>
                  <Btn variant="ghost" onClick={() => { setStep(0); setReport(null); setLeadState("idle"); }}>Run Another Audit →</Btn>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: C.syne, marginBottom: 4 }}>
                    Get your full report + free consultation
                  </div>
                  <div style={{ fontSize: 13, color: C.sub, fontFamily: C.inst, marginBottom: 20 }}>
                    Enter your details to receive the PDF and have our team contact you within 24 hours.
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div>
                      <FieldLabel>Your Full Name *</FieldLabel>
                      <AInput value={form.contactName} onChange={v => upd("contactName", v)} placeholder="e.g. Ahmed Khan" />
                    </div>
                    <div>
                      <FieldLabel>Work Email *</FieldLabel>
                      <AInput value={form.contactEmail} onChange={v => upd("contactEmail", v)} placeholder="ahmed@company.com" type="email" />
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <FieldLabel>WhatsApp / Phone (optional)</FieldLabel>
                    <AInput value={form.contactPhone} onChange={v => upd("contactPhone", v)} placeholder="+92 300 1234567" type="tel" />
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    <Btn onClick={sendLead} disabled={!form.contactName || !form.contactEmail || leadState === "sending"} style={{ flex: 1, textAlign: "center" }}>
                      {leadState === "sending" ? <span className="spin">↻</span> : "📩 Send Me My Report →"}
                    </Btn>
                    <Btn variant="ghost" onClick={printReport}>⬇ Print PDF</Btn>
                  </div>
                  {leadState === "fail" && (
                    <div style={{ marginTop: 10, fontSize: 12, color: C.red, fontFamily: C.inst }}>
                      Submission failed. Email us directly: hello@aipartners.pk
                    </div>
                  )}
                  <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: C.muted, fontFamily: C.inst }}>
                    🔒 Your data is confidential · No spam · Response within 24 hours
                  </div>
                </>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
